/**
 * Future Closure Results Component
 * Displays pro forma projections and total demand
 */

import React, { useState } from 'react';
import { FinancialValue } from './shared/FinancialValue';
import { MonteCarloParams, SensitivityParams, downloadFutureClosurePDF } from '../services/futureClosureApi';

interface FutureClosureResultsProps {
  result: any;
  reportId: string;
  calculationId?: string;
  onRunMonteCarlo: (params: MonteCarloParams) => void;
  onRunSensitivity: (params: SensitivityParams) => void;
  monteCarloLoading: boolean;
  sensitivityLoading: boolean;
  onRecalculate?: () => void;  // P3: Recalculate button callback
}

export default function FutureClosureResults({
  result,
  reportId,
  calculationId,
  onRunMonteCarlo,
  onRunSensitivity,
  monteCarloLoading,
  sensitivityLoading,
  onRecalculate
}: FutureClosureResultsProps) {
  const [showMonthlyDetails, setShowMonthlyDetails] = useState(false);
  const [showDefensibility, setShowDefensibility] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);
    setPdfError(null);
    try {
      await downloadFutureClosurePDF(reportId, calculationId);
    } catch (err: any) {
      console.error('Error downloading PDF:', err);
      setPdfError(err.response?.data?.detail || err.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  if (!result) return null;

  const { parameters, pro_forma_summary, one_time_costs, total_demand, pro_forma_monthly, continuation_percentages, data_source } = result;

  return (
    <div className="space-y-6">
      {/* P&L Data Source Indicator */}
      {continuation_percentages && (
        <div className="border rounded-md p-4 bg-[var(--color-bg-surface)] border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-3">
            <h4 className="cs-h3 flex items-center">
              <svg className="w-5 h-5 mr-2" style={{ color: 'var(--color-needs-human-text)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Expense Continuation Rates
            </h4>
            {data_source && (
              <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                {typeof data_source === 'string' ? data_source : 'Granular P&L Data'}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
            {Object.entries(continuation_percentages).map(([category, rate]) => (
              <div key={category} className="rounded-md p-3 border bg-[var(--color-bg-elevated)] border-[var(--color-border)]">
                <div className="text-xs mb-1 capitalize" style={{ color: 'var(--color-text-muted)' }}>
                  {category.replace(/_/g, ' ')}
                </div>
                <div className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                  {typeof rate === 'number' ? `${rate.toFixed(0)}%` : (rate as React.ReactNode)}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs mt-3" style={{ color: 'var(--color-text-muted)' }}>
            These rates determine what percentage of each expense continues during closure. Rates are calculated based on your uploaded P&L data and industry standards.
          </p>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Lost Profits */}
        <div className="rounded-md p-6 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <p className="text-sm font-medium mb-1 text-[var(--color-text-secondary)]">Lost Profits (36 mo)</p>
          <p className="text-3xl font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]">
            <FinancialValue value={pro_forma_summary?.total_lost_profits} cents />
          </p>
        </div>

        {/* One-Time Costs */}
        <div className="rounded-md p-6 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <p className="text-sm font-medium mb-1 text-[var(--color-text-secondary)]">One-Time Costs</p>
          <p className="text-3xl font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]">
            <FinancialValue value={one_time_costs?.total} cents />
          </p>
          <p className="text-xs mt-2 text-[var(--color-text-muted)]">
            Shutdown: <FinancialValue value={one_time_costs?.shutdown} cents /> |
            Restart: <FinancialValue value={one_time_costs?.restart} cents />
          </p>
        </div>

        {/* Total Demand */}
        <div className="rounded-md p-6 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <p className="text-sm font-medium mb-1 text-[var(--color-text-secondary)]">Total Demand</p>
          <p className="text-3xl font-bold font-mono tabular-nums text-[var(--color-needs-human-text)]">
            <FinancialValue value={total_demand} cents />
          </p>
        </div>
      </div>

      {/* Parameters Used */}
      <div className="border rounded-md p-4 bg-[var(--color-bg-surface)] border-[var(--color-border)]">
        <h4 className="cs-h3 mb-3">Calculation Parameters</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Aggression:</span>
            <span className="ml-2 font-semibold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{parameters?.aggression}/100</span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Growth Rate:</span>
            <span className="ml-2 font-semibold font-mono tabular-nums">{parameters?.growth_rate}%</span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Base Period:</span>
            <span className="ml-2 font-semibold">{result.base_period_summary?.period_type} {result.base_period_summary?.months_analyzed} mo</span>
          </div>
          <div>
            <span style={{ color: 'var(--color-text-secondary)' }}>Closure:</span>
            <span className="ml-2 font-semibold">{parameters?.closure_duration_months} months</span>
          </div>
        </div>
      </div>

      {/* Monthly Pro Forma Toggle */}
      <button
        onClick={() => setShowMonthlyDetails(!showMonthlyDetails)}
        className="w-full border rounded-md p-4 flex items-center justify-between transition-colors"
        style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
      >
        <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>36-Month Pro Forma Details</span>
        <svg
          className={`w-5 h-5 transition-transform ${showMonthlyDetails ? 'transform rotate-180' : ''}`}
          style={{ color: 'var(--color-text-dim)' }}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Monthly Pro Forma Table */}
      {showMonthlyDetails && pro_forma_monthly && (
        <div className="border rounded-md overflow-hidden bg-[var(--color-bg-surface)] border-[var(--color-border)]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[color:var(--color-border-faint)]">
              <thead style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Month</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Expenses</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase" style={{ color: 'var(--color-text-label)' }}>Lost Profit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--color-border-faint)]" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                {pro_forma_monthly.map((month: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {month.month_number}. {month.month_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={month.projected_revenue} cents />
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                      <FinancialValue value={month.continued_expenses} cents />
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                      <FinancialValue value={month.lost_profit} cents />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Defensibility Notes Toggle */}
      <button
        onClick={() => setShowDefensibility(!showDefensibility)}
        className="w-full border rounded-md p-4 flex items-center justify-between transition-colors"
        style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}
      >
        <span className="font-medium" style={{ color: 'var(--color-verified)' }}>Defensibility Notes</span>
        <svg
          className={`w-5 h-5 transition-transform ${showDefensibility ? 'transform rotate-180' : ''}`}
          style={{ color: 'var(--color-verified)' }}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {showDefensibility && result.defensibility_notes && (
        <div className="border rounded-md p-4" style={{ backgroundColor: 'transparent', borderColor: 'var(--color-verified)' }}>
          <ul className="space-y-2">
            {result.defensibility_notes.map((note: string, idx: number) => (
              <li key={idx} className="flex items-start space-x-2 text-sm" style={{ color: 'var(--color-verified)' }}>
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-verified)' }} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>{note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Sensitivity Analysis Button */}
        <button
          onClick={() => onRunSensitivity({ 
            aggression: parameters?.aggression || 50,
            use_best_base: result.parameters?.use_best_base,
            base_months: result.parameters?.base_months
          })}
          disabled={sensitivityLoading}
          className={`py-4 rounded-md font-semibold transition-all ${
            sensitivityLoading
              ? 'cursor-not-allowed'
              : ''
          }`}
          style={
            sensitivityLoading
              ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)' }
              : { backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-border)' }
          }
        >
          {sensitivityLoading ? (
            <span className="flex items-center justify-center space-x-2">
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Analyzing...</span>
            </span>
          ) : (
            <span className="flex items-center justify-center space-x-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <span>Run Sensitivity Analysis (Tornado Chart)</span>
            </span>
          )}
        </button>

        {/* Monte Carlo Button */}
        <button
          onClick={() => onRunMonteCarlo({ aggression: parameters?.aggression || 50 })}
          disabled={monteCarloLoading}
          className={`py-4 rounded-md font-semibold transition-all ${
            monteCarloLoading
              ? 'cursor-not-allowed'
              : ''
          }`}
          style={
            monteCarloLoading
              ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)' }
              : { backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-border)' }
          }
        >
        {monteCarloLoading ? (
          <span className="flex items-center justify-center space-x-2">
            <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Running 50,000 Simulations...</span>
          </span>
        ) : (
          <span className="flex items-center justify-center space-x-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <span>Run Monte Carlo Simulation (P10/P50/P90)</span>
          </span>
        )}
      </button>
      </div>

      {/* Action Buttons Section */}
      <div className="mt-6 pt-6 border-t" style={{ borderColor: 'var(--color-border)' }}>
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>Analysis Actions</p>
            <p>Recalculate with different parameters or export to PDF.</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Recalculate Button */}
            {onRecalculate && (
              <button
                onClick={onRecalculate}
                className="flex items-center space-x-2 px-5 py-3 rounded-md font-semibold border transition-all"
                style={{ color: 'var(--color-needs-human-text)', borderColor: 'var(--color-brand)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Recalculate</span>
              </button>
            )}
            {/* Download PDF Button */}
            <button
              onClick={handleDownloadPDF}
              disabled={pdfLoading}
              className={`flex items-center space-x-2 px-6 py-3 rounded-md font-semibold transition-all ${
                pdfLoading
                  ? 'cursor-not-allowed'
                  : ''
              }`}
              style={
                pdfLoading
                  ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-ghost)' }
                  : { backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }
              }
            >
            {pdfLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Generating PDF...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                <span>Download PDF Report</span>
              </>
            )}
            </button>
          </div>
        </div>
        {pdfError && (
          <div className="mt-3 border rounded-md p-3 text-sm bg-[var(--color-bg-surface)] border-[var(--color-border)]" style={{ color: 'var(--color-needs-human-text)' }}>
            {pdfError}
          </div>
        )}
      </div>
    </div>
  );
}
